import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  TextField,
  IconButton,
  Divider,
  Stack,
  CircularProgress,
  InputAdornment,
  FormLabel,
  Alert,
  Grid,
  FormControlLabel,
  Checkbox,
  Switch,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Close,
  Image,
  CurrencyRupee,
  Delete,
} from "@mui/icons-material";
import { ShieldCheck } from "lucide-react";
import Axios from "../../Common/api/axiosConfig";
import { formLabelStyles } from "./styles/formLabelStyles";
import { textFieldStyles } from "./styles/textfieldStyles";
import { uploadLink } from "../../utils/constants";
import { CategoryItem, getStoreCategories } from "../../utils/categoryHelper";
import {
  normalizeAmazonUrl,
  getStoredAmazonLink,
  setStoredAmazonLink,
  removeStoredAmazonLink,
  AmazonIcon,
} from "../../utils/amazonHelper";
import {
  COMMON_WARRANTY_OPTIONS,
  getStoredWarrantyInfo,
  setStoredWarrantyInfo,
  parseBooleanValue,
} from "../../utils/warrantyHelper";

interface AddProductProps {
  open: boolean;
  onClose: () => void;
  mode: "create" | "edit";
  productData?: any;
  onSubmit?: (data: any) => void;
  fetchProducts?: () => void;
  categories?: CategoryItem[];
}

interface ProductFormValues {
  name: string;
  description: string;
  price: string;
  category: string;
  amazonLink: string;
  inStock: boolean;
  hasWarranty: boolean;
  warrantyPeriod: string;
  hidden: boolean;
  image: File[];
}

const AddProduct: React.FC<AddProductProps> = ({
  open,
  onClose,
  mode,
  productData,
  onSubmit,
  fetchProducts,
  categories: propCategories,
}) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [apiSuccess, setApiSuccess] = useState<string>("");

  const storeCategories = propCategories || getStoreCategories();
  const categoryOptions = Array.from(
    new Set([
      ...storeCategories.map((c) => c.name),
      "Hair Care",
      "Personal Care",
      "Wellness & Health",
      "Refurbished Tech",
      "Special Offers",
    ])
  );

  const [formData, setFormData] = useState<ProductFormValues>({
    name: "",
    description: "",
    price: "",
    category: "",
    amazonLink: "",
    inStock: true,
    hasWarranty: false,
    warrantyPeriod: "1 Year Warranty",
    hidden: false,
    image: [],
  });

  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [removedImages, setRemovedImages] = useState<string[]>([]); // Track removed images
  const [errors, setErrors] = useState<
    Partial<Record<keyof ProductFormValues | "imageGeneral", string>>
  >({});

  // Initialize form data when editing
  useEffect(() => {
    if (mode === "edit" && productData) {
      console.log("Setting edit data:", productData);

      const storedWarranty = getStoredWarrantyInfo(productData._id);
      const initialHasWarranty =
        parseBooleanValue(
          productData.hasWarranty !== undefined
            ? productData.hasWarranty
            : productData.has_warranty !== undefined
            ? productData.has_warranty
            : storedWarranty?.hasWarranty
        ) ?? false;

      const initialWarrantyPeriod =
        productData.warrantyPeriod ||
        productData.warranty_period ||
        storedWarranty?.warrantyPeriod ||
        "1 Year Warranty";

      setFormData({
        name: productData.name || "",
        description: productData.description || "",
        price: productData.price?.toString() || "",
        category: productData.category || "",
        amazonLink:
          productData.amazonLink ||
          productData.amazon_link ||
          getStoredAmazonLink(productData._id) ||
          "",
        inStock:
          productData.inStock !== false && productData.in_stock !== false,
        hasWarranty: initialHasWarranty,
        warrantyPeriod: initialWarrantyPeriod,
        hidden: productData.ishide || false,
        image: [], // Keep empty for new uploads
      });

      // Handle existing images for edit mode
      if (productData.images && productData.images.length > 0) {
        const existingImagePreviews = productData.images.map(
          (image: string) => {
            // Check if image already has full URL or needs to be constructed
            if (image.startsWith("http")) {
              return image;
            } else {
              return `${uploadLink}${image}`;
            }
          }
        );
        console.log("Setting image previews:", existingImagePreviews);
        setImagePreviews(existingImagePreviews);
      } else {
        setImagePreviews([]);
      }

      // Reset removed images when editing starts
      setRemovedImages([]);
    } else if (mode === "create") {
      // Only reset for create mode
      setFormData({
        name: "",
        description: "",
        price: "",
        category: "",
        amazonLink: "",
        inStock: true,
        hasWarranty: false,
        warrantyPeriod: "1 Year Warranty",
        hidden: false,
        image: [],
      });
      setImagePreviews([]);
      setRemovedImages([]);
    }
  }, [mode, productData]);

  // Reset form when drawer closes
  useEffect(() => {
    if (!open) {
      setErrors({});
      setApiError("");
      setApiSuccess("");
      setLoading(false);
    }
  }, [open]);

  // Validation Logic
  const validateField = (
    name: string,
    value: string | File[]
  ): string | undefined => {
    switch (name) {
      case "name":
        if (typeof value === "string") {
          if (!value.trim()) return "Product name is required.";
          if (value.length > 100)
            return "Name must be less than 100 characters.";
        }
        return;
      case "description":
        if (typeof value === "string") {
          if (!value.trim()) return "Description is required.";
          if (value.length > 300)
            return "Description must be less than 300 characters.";
        }
        return;
      case "price":
        if (typeof value === "string") {
          if (!value.trim()) return "Price is required.";
          if (isNaN(Number(value)) || Number(value) <= 0)
            return "Enter a valid price greater than 0.";
        }
        return;
      case "category":
        if (typeof value === "string") {
          if (!value.trim()) return "Category is required.";
        }
        return;
      case "image":
        if (Array.isArray(value) && value.length > 0) {
          const validTypes = [
            "image/jpeg",
            "image/jpg",
            "image/png",
            "image/webp",
          ];
          for (const file of value) {
            if (file instanceof File) {
              if (!validTypes.includes(file.type)) {
                return "Only JPG, JPEG, PNG, and WEBP images are allowed.";
              }
              if (file.size > 5 * 1024 * 1024) {
                return "Each image must be less than 5MB.";
              }
            }
          }
        }
        return;
      default:
        return;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<
      Record<keyof ProductFormValues | "imageGeneral", string>
    > = {};

    // Validate text fields
    ["name", "description", "price", "category"].forEach((key) => {
      const error = validateField(
        key,
        formData[key as keyof ProductFormValues] as string
      );
      if (error) newErrors[key as keyof ProductFormValues] = error;
    });

    // Validate image (at least one image required for create mode)
    // For edit mode, allow existing images (imagePreviews) or new uploads
    const hasImages = formData.image.length > 0 || imagePreviews.length > 0;
    if (mode === "create" && !hasImages) {
      newErrors.imageGeneral = "Please upload at least one image.";
    }

    if (formData.image.length > 0) {
      const imageError = validateField("image", formData.image);
      if (imageError) newErrors.image = imageError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));

      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }

    setApiError("");
  };

  const handleMultipleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate files
    const error = validateField("image", files);
    if (error) {
      setErrors((prev) => ({ ...prev, image: error }));
      return;
    }

    // Add new images to existing ones
    const newImages = [...formData.image, ...files];
    setFormData((prev) => ({ ...prev, image: newImages }));
    setErrors((prev) => ({
      ...prev,
      image: undefined,
      imageGeneral: undefined,
    }));

    // Create previews for new images
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result as string]);
      };
      reader.readAsDataURL(file);
    });

    setApiError("");
    // Reset input
    e.target.value = "";
  };

  const handleRemoveImage = (index: number) => {
    // Check if it's a newly uploaded image or existing preview
    const totalExistingPreviews = imagePreviews.length - formData.image.length;

    if (index < totalExistingPreviews) {
      // It's an existing image preview - track it for removal
      const imageToRemove = imagePreviews[index];

      // Extract the original image path (remove uploadLink if present)
      let originalImagePath = imageToRemove;
      if (imageToRemove.includes(uploadLink)) {
        originalImagePath = imageToRemove.replace(uploadLink, "");
      }

      // Add to removed images array
      setRemovedImages((prev) => [...prev, originalImagePath]);

      // Remove from previews
      const newPreviews = [...imagePreviews];
      newPreviews.splice(index, 1);
      setImagePreviews(newPreviews);
    } else {
      // It's a newly uploaded image - remove from both formData and previews
      const newImages = [...formData.image];
      const newPreviews = [...imagePreviews];

      const fileIndex = index - totalExistingPreviews;
      newImages.splice(fileIndex, 1);
      newPreviews.splice(index, 1);

      setFormData((prev) => ({ ...prev, image: newImages }));
      setImagePreviews(newPreviews);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setApiError("");
    setApiSuccess("");

    try {
      // Create FormData
      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("description", formData.description.trim());
      formDataToSend.append("price", formData.price);
      formDataToSend.append("category", formData.category.trim());
      formDataToSend.append("ishide", formData.hidden ? "true" : "false");
      formDataToSend.append("inStock", formData.inStock ? "true" : "false");
      formDataToSend.append("hasWarranty", formData.hasWarranty ? "true" : "false");
      formDataToSend.append("has_warranty", formData.hasWarranty ? "true" : "false");
      formDataToSend.append(
        "warrantyPeriod",
        formData.hasWarranty ? formData.warrantyPeriod.trim() : ""
      );
      formDataToSend.append(
        "warranty_period",
        formData.hasWarranty ? formData.warrantyPeriod.trim() : ""
      );

      // Add multiple images
      formData.image.forEach((image) => {
        formDataToSend.append("image", image);
      });

      // Amazon Link for Order Now
      const trimmedAmazonLink = formData.amazonLink
        ? normalizeAmazonUrl(formData.amazonLink.trim())
        : "";
      formDataToSend.append("amazonLink", trimmedAmazonLink);
      formDataToSend.append("amazon_link", trimmedAmazonLink);

      // For edit mode, add removed images
      if (mode === "edit" && removedImages.length > 0) {
        formDataToSend.append("removedImages", JSON.stringify(removedImages));
      }

      // API endpoint
      const endpoint =
        mode === "create" ? "/product" : `/product/${productData?._id}`;

      const method = mode === "create" ? "post" : "patch";

      // Use Axios with dynamic method
      const response = await Axios[method](endpoint, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Persist amazonLink & warranty locally as well
      const savedProductId = response.data?.data?._id || productData?._id;
      if (savedProductId) {
        if (trimmedAmazonLink) {
          setStoredAmazonLink(savedProductId, trimmedAmazonLink);
        } else {
          removeStoredAmazonLink(savedProductId);
        }

        setStoredWarrantyInfo(savedProductId, {
          hasWarranty: formData.hasWarranty,
          warrantyPeriod: formData.hasWarranty
            ? formData.warrantyPeriod.trim() || "1 Year Warranty"
            : "",
        });
      }

      setApiSuccess(
        mode === "create"
          ? "Product created successfully!"
          : "Product updated successfully!"
      );

      // Call parent callback with the actual API response data
      const resultData = response.data?.data
        ? {
            ...response.data,
            data: {
              ...response.data.data,
              amazonLink: trimmedAmazonLink,
              inStock: formData.inStock,
              hasWarranty: formData.hasWarranty,
              has_warranty: formData.hasWarranty,
              warrantyPeriod: formData.hasWarranty
                ? formData.warrantyPeriod.trim() || "1 Year Warranty"
                : "",
              warranty_period: formData.hasWarranty
                ? formData.warrantyPeriod.trim() || "1 Year Warranty"
                : "",
            },
          }
        : {
            ...response.data,
            amazonLink: trimmedAmazonLink,
            inStock: formData.inStock,
            hasWarranty: formData.hasWarranty,
            has_warranty: formData.hasWarranty,
            warrantyPeriod: formData.hasWarranty
              ? formData.warrantyPeriod.trim() || "1 Year Warranty"
              : "",
            warranty_period: formData.hasWarranty
              ? formData.warrantyPeriod.trim() || "1 Year Warranty"
              : "",
          };
      onSubmit?.(resultData);
      fetchProducts?.();

      // Close drawer after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      console.error("API Error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "An unexpected error occurred. Please try again.";
      setApiError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "85%", md: "65%", lg: "45%" },
          p: 3,
          borderRadius: "12px 0 0 12px",
          boxSizing: "border-box",
          boxShadow: theme.shadows[10],
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
        mt={3}
      >
        <Typography variant="h6" fontWeight={600}>
          {mode === "create" ? "Add Product" : "Edit Product"}
        </Typography>
        <IconButton onClick={onClose}>
          <Close sx={{ color: "#f44336" }} />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* API Messages */}
      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError("")}>
          {apiError}
        </Alert>
      )}
      {apiSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {apiSuccess}
        </Alert>
      )}

      {/* Form */}
      <Stack spacing={3}>
        {/* Product Name */}
        <Box>
          <FormLabel required sx={formLabelStyles}>
            Product Name
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            name="name"
            placeholder="Enter product name"
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={textFieldStyles(theme)}
          />
        </Box>
        {/* Description */}
        <Box>
          <FormLabel required sx={formLabelStyles}>
            Description
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            name="description"
            multiline
            minRows={3}
            placeholder="Enter product description"
            value={formData.description}
            onChange={handleInputChange}
            error={!!errors.description}
            helperText={errors.description}
            sx={textFieldStyles(theme)}
          />
        </Box>
        {/* Price */}
        <Box>
          <FormLabel required sx={formLabelStyles}>
            Price (₹)
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            name="price"
            type="number"
            placeholder="Enter price"
            value={formData.price}
            onChange={handleInputChange}
            error={!!errors.price}
            helperText={errors.price}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CurrencyRupee fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles(theme)}
          />
        </Box>
        {/* Category */}
        <Box>
          <FormLabel required sx={formLabelStyles}>
            Category
          </FormLabel>
          <Autocomplete
            freeSolo
            options={categoryOptions}
            value={formData.category}
            onChange={(_, newValue) => {
              setFormData((prev) => ({ ...prev, category: newValue || "" }));
              if (errors.category) {
                setErrors((prev) => ({ ...prev, category: undefined }));
              }
            }}
            onInputChange={(_, newInputValue) => {
              setFormData((prev) => ({ ...prev, category: newInputValue || "" }));
              if (errors.category) {
                setErrors((prev) => ({ ...prev, category: undefined }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder="Select or enter category (e.g., Hair Care)"
                error={!!errors.category}
                helperText={errors.category}
                sx={textFieldStyles(theme)}
              />
            )}
          />
        </Box>
        {/* Amazon Link (for Order Now) */}
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <FormLabel sx={formLabelStyles}>
              Amazon Link (Order Now)
            </FormLabel>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Optional
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            name="amazonLink"
            placeholder="https://www.amazon.in/dp/B0..."
            value={formData.amazonLink}
            onChange={handleInputChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AmazonIcon size={18} color="#ea580c" />
                </InputAdornment>
              ),
            }}
            helperText="When provided, customers clicking 'Order Now' will go directly to this Amazon product page"
            sx={textFieldStyles(theme)}
          />
        </Box>
        {/* Availability / In Stock */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: formData.inStock ? "#f0fdf4" : "#fef2f2",
            border: `1px solid ${formData.inStock ? "#bbf7d0" : "#fecaca"}`,
            transition: "all 0.2s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 700,
                  color: formData.inStock ? "#166534" : "#991b1b",
                }}
              >
                {formData.inStock ? "● In Stock" : "○ Out of Stock"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: formData.inStock ? "#15803d" : "#b91c1c",
                  display: "block",
                  mt: 0.25,
                }}
              >
                {formData.inStock
                  ? "Product is available for customers to order."
                  : "Shows as Out of Stock on store; ordering links are disabled."}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.inStock}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      inStock: e.target.checked,
                    }))
                  }
                  color="success"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>
        </Box>
        {/* Product Warranty */}
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: formData.hasWarranty ? "#eff6ff" : "#f8fafc",
            border: `1px solid ${formData.hasWarranty ? "#bfdbfe" : "#e2e8f0"}`,
            transition: "all 0.2s ease",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              <Box
                sx={{
                  p: 0.8,
                  borderRadius: 1.5,
                  bgcolor: formData.hasWarranty ? "#dbeafe" : "#e2e8f0",
                  color: formData.hasWarranty ? "#1d4ed8" : "#64748b",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShieldCheck size={20} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: formData.hasWarranty ? "#1e40af" : "#334155",
                  }}
                >
                  {formData.hasWarranty
                    ? "● Warranty Covered"
                    : "○ No Warranty"}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: formData.hasWarranty ? "#1d4ed8" : "#64748b",
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  {formData.hasWarranty
                    ? "Product comes under warranty. Badge is shown on product cards."
                    : "Product does not come under warranty."}
                </Typography>
              </Box>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasWarranty}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      hasWarranty: e.target.checked,
                    }))
                  }
                  color="primary"
                />
              }
              label=""
              sx={{ m: 0 }}
            />
          </Box>

          {formData.hasWarranty && (
            <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #bfdbfe" }}>
              <FormLabel
                sx={{
                  ...formLabelStyles,
                  fontSize: "0.82rem",
                  mb: 1,
                  display: "block",
                  color: "#1e40af",
                }}
              >
                Warranty Duration & Details
              </FormLabel>

              {/* Quick Preset Chips */}
              <Box
                sx={{
                  display: "flex",
                  gap: 0.75,
                  flexWrap: "wrap",
                  mb: 1.5,
                }}
              >
                {COMMON_WARRANTY_OPTIONS.map((opt) => (
                  <Chip
                    key={opt}
                    label={opt}
                    size="small"
                    clickable
                    color={formData.warrantyPeriod === opt ? "primary" : "default"}
                    variant={formData.warrantyPeriod === opt ? "filled" : "outlined"}
                    onClick={() =>
                      setFormData((prev) => ({
                        ...prev,
                        warrantyPeriod: opt,
                      }))
                    }
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      borderRadius: 1.5,
                    }}
                  />
                ))}
              </Box>

              <TextField
                fullWidth
                size="small"
                name="warrantyPeriod"
                placeholder="e.g. 1 Year Warranty, 6 Months Replacement"
                value={formData.warrantyPeriod}
                onChange={handleInputChange}
                sx={textFieldStyles(theme)}
                helperText="Coverage duration displayed on admin product card and store product page"
              />
            </Box>
          )}
        </Box>
        {/* Hidden */}
        <Box>
          <FormLabel required sx={formLabelStyles}>
            Hide Product
          </FormLabel>
          <FormControlLabel
            control={
              <Checkbox
                name="hidden"
                checked={formData.hidden}
                onChange={handleInputChange}
              />
            }
            label="Hide product from customers"
          />
        </Box>
        {/* Product Images */}
        <Box>
          <FormLabel required={mode === "create"} sx={formLabelStyles}>
            Product Images {mode === "create" && "*"}
          </FormLabel>

          {/* Image Previews Grid */}
          {imagePreviews.length > 0 && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {imagePreviews.map((preview, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      paddingTop: "100%",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid #e0e0e0",
                      bgcolor: "#f5f5f5",
                    }}
                  >
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      crossOrigin="anonymous"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 1)",
                        },
                      }}
                    >
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Upload Button */}
          <Button
            variant="outlined"
            component="label"
            startIcon={<Image />}
            fullWidth
            sx={{
              textTransform: "none",
              borderRadius: 2,
              borderColor:
                errors.image || errors.imageGeneral ? "#d32f2f" : "#ccc",
              color: "#555",
              py: 1.5,
            }}
          >
            Upload Images
            <input
              type="file"
              hidden
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleMultipleImageUpload}
            />
          </Button>

          {(errors.image || errors.imageGeneral) && (
            <Typography
              variant="caption"
              color="error"
              sx={{ mt: 1, display: "block" }}
            >
              {errors.image || errors.imageGeneral}
            </Typography>
          )}
        </Box>
        {/* Submit */}
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: 3,
              px: 3,
              py: 1.1,
              background: "linear-gradient(135deg, #2196f3, #42a5f5)",
              color: "#fff",
              boxShadow: "0px 3px 8px rgba(0,0,0,0.2)",
              "&:hover": {
                background: "linear-gradient(135deg, #1e88e5, #64b5f6)",
              },
              "&:disabled": {
                background: "#ccc",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: "#fff" }} />
            ) : mode === "create" ? (
              "Add Product"
            ) : (
              "Save Changes"
            )}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default AddProduct;
